import { useEffect } from "preact/hooks";
import { siteOrigin } from "./socialMeta.ts";

type PageMeta = {
  title: string;
  description?: string;
  image?: string;
  type?: "website" | "article";
  twitterCard?: "summary" | "summary_large_image";
};

const setMetaContent = (selector: string, content: string) => {
  const el = document.querySelector(selector);
  if (el) el.setAttribute("content", content);
};

const upsertLink = (rel: string, href: string) => {
  const existing = document.querySelector(`link[rel="${rel}"]`);
  if (existing) {
    existing.setAttribute("href", href);
    return;
  }
  const link = document.createElement("link");
  link.setAttribute("rel", rel);
  link.setAttribute("href", href);
  document.head.appendChild(link);
};

const absoluteUrl = () => {
  const { pathname, search, hash } = globalThis.location;
  return `${siteOrigin}${pathname}${search}${hash}`;
};

const absoluteImage = (image: string) =>
  image.startsWith("http") ? image : `${siteOrigin}${image}`;

const applyPageMeta = (
  { title, description, image, type, twitterCard }: PageMeta,
) => {
  document.title = title;
  setMetaContent('meta[property="og:title"]', title);
  setMetaContent('meta[name="twitter:title"]', title);
  if (description) {
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[name="twitter:description"]', description);
  }
  if (image) {
    const abs = absoluteImage(image);
    setMetaContent('meta[property="og:image"]', abs);
    setMetaContent('meta[name="twitter:image"]', abs);
  }
  if (type) setMetaContent('meta[property="og:type"]', type);
  if (twitterCard) setMetaContent('meta[name="twitter:card"]', twitterCard);
  const url = absoluteUrl();
  setMetaContent('meta[property="og:url"]', url);
  upsertLink("canonical", url);
};

export const usePageMeta = (meta: PageMeta) => {
  useEffect(() => {
    if (typeof document === "undefined") return;
    applyPageMeta(meta);
    const update = () => applyPageMeta(meta);
    globalThis.addEventListener("hashchange", update);
    globalThis.addEventListener("popstate", update);
    return () => {
      globalThis.removeEventListener("hashchange", update);
      globalThis.removeEventListener("popstate", update);
    };
  }, [
    meta.title,
    meta.description,
    meta.image,
    meta.type,
    meta.twitterCard,
  ]);
};
